import type {
  AnalyticsRange,
  StaffAnalyticsAccess,
  StaffAnalyticsRecentVisits,
} from "@lafayette/schemas";
import { useId } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { Skeleton } from "../../ui/skeleton";
import { firstNameOf, stepSentence } from "../event-sentences";
import { clockText, visitSpan } from "../events/events-model";
import { useRecentVisits } from "../events/events-queries";
import { eventsHref, exploreVisitHref } from "../events/explore-link";
import { countText, durationText } from "../format";
import type { CustomerView } from "../queries";

// "What <customer> did recently" (Paper "What Anika did recently", CHT-2030): the last three visits of the phone app, each with its device, its
// length, its screen count and up to five steps written as sentences, then "Zoom into this visit" (Explore, at that visit). The sentences are the
// closed templates of `event-sentences.ts`: closed values only, never a free text. It reads the server-side read-back, so an opted-out customer
// gets NO card (the Overview says in words that only records are shown) and the card never guesses a visit.

type Recent = StaffAnalyticsRecentVisits;
type RecentVisit = Recent["visits"][number];

function VisitPanel({
  entry,
  access,
}: Readonly<{ entry: RecentVisit; access: StaffAnalyticsAccess }>) {
  const { visit, steps } = entry;
  const tz = access.window.tz;
  const device =
    visit.platform === "ios" ? "iOS" : visit.platform === "android" ? "Android" : "Unknown device";
  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-xl border border-hairline bg-muted p-3.5">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-card"
        >
          <Icon name="smart-phone-01" size="md" />
        </span>
        <div className="min-w-0">
          <h3 className="m-0 text-sm leading-5 font-semibold">
            {visitSpan(visit.startedAt, visit.endedAt, tz, access.window.to)}
          </h3>
          <p className="m-0 text-xs leading-4 text-muted-foreground">
            {device} · {durationText(visit.durationSeconds)} · {countText(visit.screenCount)}{" "}
            {visit.screenCount === 1 ? "screen" : "screens"}
          </p>
        </div>
      </div>
      {steps === null ? (
        <p className="m-0 grow text-[13px] leading-[18px] text-muted-foreground" role="status">
          The steps of this visit could not be read right now.
        </p>
      ) : steps.length === 0 ? (
        <p className="m-0 grow text-[13px] leading-[18px] text-muted-foreground">
          No steps to show.
        </p>
      ) : (
        <ol className="m-0 flex grow list-none flex-col gap-2 p-0">
          {steps.map((step) => (
            <li key={step.uuid} className="flex gap-2.5">
              <span className="w-10 shrink-0 text-xs leading-[18px] text-muted-foreground">
                {clockText(step.timestamp, tz)}
              </span>
              <span className="text-[13px] leading-[18px]">
                {stepSentence(step.event, step.properties)}.
              </span>
            </li>
          ))}
        </ol>
      )}
      <div className="flex items-center gap-1.5 border-t border-hairline pt-1">
        <Icon name="zoom-in-area" size="sm" className="shrink-0 text-link" />
        <Link
          to={exploreVisitHref(access.customerId, visit, access.window.to)}
          className="text-[13px] leading-7 font-semibold text-link no-underline hover:underline"
        >
          Zoom into this visit
        </Link>
      </div>
    </li>
  );
}

export function RecentActivityCard({
  view,
  customerId,
  range,
  access,
}: Readonly<{
  view: CustomerView;
  customerId: string;
  range: AnalyticsRange;
  access: StaffAnalyticsAccess;
}>) {
  const titleId = useId();
  const recent = useRecentVisits(view, customerId, range);
  const name = firstNameOf(access.displayName);
  const data = recent.data;
  // An opted-out customer, or no read-back: no card. The Overview already says why in words.
  if (
    data !== undefined &&
    (data.availability === "OPTED_OUT" || data.availability === "ANALYTICS_OFF")
  ) {
    return null;
  }
  if (recent.isError) return null;
  return (
    <Card className="rounded-2xl lg:col-span-3">
      <section aria-labelledby={titleId}>
        <CardHeader className="grid-cols-1 gap-2 pb-3 sm:flex sm:flex-wrap sm:items-center sm:justify-between">
          <div className="grid gap-0.5">
            <CardTitle as="h2" id={titleId} className="text-xl leading-6">
              What {name} did recently
            </CardTitle>
            <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
              In plain words, grouped by visit. The raw events are in the Events tab.
            </p>
          </div>
          {data?.eventCount === null || data?.eventCount === undefined ? null : (
            <Button asChild variant="outline" className="rounded-[10px]">
              <Link to={eventsHref(customerId, range)}>
                <Icon name="note-01" size="sm" />
                Open all {countText(data.eventCount)} events
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {data === undefined ? (
            <div role="status" className="grid gap-2 lg:grid-cols-3">
              <span className="sr-only">Loading the recent visits</span>
              <Skeleton className="h-40" />
              <Skeleton className="hidden h-40 lg:block" />
              <Skeleton className="hidden h-40 lg:block" />
            </div>
          ) : (
            <>
              {data.availability === "VENDOR_UNAVAILABLE" ? (
                <Notice tone="warning" role="status">
                  Some app events could not be read right now. The visits are shown without their
                  steps where that happened.
                </Notice>
              ) : null}
              {data.visits.length === 0 ? (
                <p className="m-0 text-sm text-muted-foreground">
                  {name} has no visit of the app in the last {access.retentionMonths} months.
                </p>
              ) : (
                <ul className="m-0 grid list-none gap-4 p-0 lg:grid-cols-3">
                  {data.visits.map((entry) => (
                    <VisitPanel key={entry.visit.visitId} entry={entry} access={access} />
                  ))}
                </ul>
              )}
            </>
          )}
        </CardContent>
      </section>
    </Card>
  );
}
